import { useState } from 'react'
import {
  Box,
  Typography,
  Link,
  Button,
  Collapse,
  IconButton,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { StartAndMonitorFlow } from '../../contentService/FlowStartandMonitor'
import { SummaryTable } from '../../SummaryTable'
import {
  isPipelineCompleteAtom,
  prepareResultAtom,
  processResultAtom,
} from './atoms'
import { useRetryFailedLessons, useProcessFlowComplete } from './hooks'
import type { StageProps, SlidesPrepareResultData } from './types'

function lessonDisplayTitle(
  prepareResult: SlidesPrepareResultData | null,
  lessonId: string,
  fallbackTitle: string,
): string {
  const lesson = prepareResult?.lessons?.find((l) => l.lesson_id === lessonId)
  return lesson?.lesson_title ?? fallbackTitle
}

export function ResultsDisplay({ treeId, versionId }: StageProps) {
  const isPipelineComplete = useAtomValue(isPipelineCompleteAtom)
  const prepareResult = useAtomValue(prepareResultAtom)
  const processResult = useAtomValue(processResultAtom)
  const retryStartFlowFunction = useRetryFailedLessons(treeId, versionId)
  const { handleFlowComplete } = useProcessFlowComplete(treeId, versionId)
  const [showGenerated, setShowGenerated] = useState(false)
  const [showFailed, setShowFailed] = useState(false)

  if (!isPipelineComplete || !processResult) return null

  const hasErrors = processResult.errors.length > 0
  const hasSuccess = processResult.converted_lessons.length > 0

  return (
    <Box border={1} borderColor="grey.300" borderRadius={1} p={2} mb={2}>
      <Typography variant="h6" gutterBottom sx={{ fontStyle: 'italic' }}>
        Results
      </Typography>

      {hasSuccess && (
        <Typography variant="body1" color="success.main" sx={{ mb: 0.5 }}>
          {processResult.converted_lessons.length} Slide Deck
          {processResult.converted_lessons.length !== 1 ? 's' : ''} Generated
        </Typography>
      )}

      {hasErrors && (
        <Typography variant="body1" color="error" sx={{ mb: 0.5 }}>
          {processResult.errors.length} Lesson
          {processResult.errors.length !== 1 ? 's' : ''} Failed
        </Typography>
      )}

      {/* Generated Slide Decks Details */}
      {hasSuccess && (
        <Box sx={{ mt: 1 }}>
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle2" color="success.main">
              Generated Slide Decks
            </Typography>
            <IconButton
              size="small"
              onClick={() => setShowGenerated(!showGenerated)}
            >
              {showGenerated ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showGenerated}>
            <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
              <SummaryTable
                headers={['Lesson Title', 'Slide Deck']}
                rows={processResult.converted_lessons.map((lesson) => ({
                  cells: [
                    lessonDisplayTitle(
                      prepareResult,
                      lesson.lesson_id,
                      lesson.lesson_title ||
                        lesson.lesson_id ||
                        'Unknown lesson',
                    ),
                    <Link
                      key={lesson.slide_id}
                      href={`https://docs.google.com/presentation/d/${lesson.slide_id}/edit`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {lesson.slide_title}
                    </Link>,
                  ],
                }))}
              />
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Failed Lessons Details */}
      {hasErrors && (
        <Box sx={{ mt: 1 }}>
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle2" color="error">
              Failed Lessons
            </Typography>
            <IconButton size="small" onClick={() => setShowFailed(!showFailed)}>
              {showFailed ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showFailed}>
            <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
              <SummaryTable
                headers={['Lesson Title', 'Error']}
                rows={processResult.errors.map((err) => ({
                  cells: [
                    lessonDisplayTitle(
                      prepareResult,
                      err.lesson_id,
                      err.lesson_title || err.lesson_id || 'Unknown lesson',
                    ),
                    <Box
                      key={err.lesson_id}
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        border: 1,
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        bgcolor: 'grey.50',
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'inherit',
                      }}
                    >
                      {err.error}
                    </Box>,
                  ],
                }))}
              />
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Actions */}
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
        }}
      >
        {prepareResult && (
          <Button
            variant="outlined"
            size="medium"
            href={prepareResult.directory_link}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ minWidth: 240 }}
          >
            Open in Google Drive
          </Button>
        )}
        {hasErrors && (
          <StartAndMonitorFlow
            disabled={false}
            buttonText="Regenerate Failed Slides"
            processingText="Regenerating failed slides..."
            startFlowFunction={retryStartFlowFunction}
            onFlowComplete={handleFlowComplete}
            pollingIntervalMs={2000}
            buttonSize="medium"
            buttonSx={{ minWidth: 240 }}
          />
        )}
      </Box>
    </Box>
  )
}
